/*
 * Sole Inspires — storefront functionality.
 *
 * Two jobs, one shared data source (the ?view=si-feed collection feed):
 *
 *  1. SHOP BY SIZE — Shopify only hands the theme the first 100 values of a
 *     filter, and COPYT's condition/box wording pushes the store past that.
 *     This reads every SELLABLE size value from the feed and rebuilds the
 *     size buttons, so no in-stock pair is ever unreachable.
 *
 *  2. BRAND MENUS — removes any brand link whose brand has no sellable
 *     products right now, so the menu never leads to an empty page.
 *
 * Read-only. Talks only to this store's own pages. No outside requests,
 * no tracking, no customer or order data.
 *
 * Size rules (Brandon, Sep 2026). Only the size before the first " - " counts:
 *   6.5M/8W -> 6.5 | 8W -> 6.5 (-1.5) | 6.5Y -> 6.5 (>=3.5Y) | <3.5Y -> Kids
 *   7C / 8K -> Kids | EU36/37.5/40/42/47 -> approved ranges | bare <=17 -> shoe
 *   bare >=28 -> clothing waist | XXS..XXXL -> clothing | OS -> One Size
 * Anything unrecognised goes to "Other" — visible, never dropped.
 */
(function () {
  'use strict';

  /* ================= shared: the data feed ================= */

  var CACHE_MS = 10 * 60 * 1000;
  var feeds = {};

  function cacheGet(key) {
    try {
      var o = JSON.parse(sessionStorage.getItem(key) || 'null');
      return o && (Date.now() - o.t < CACHE_MS) ? o.v : null;
    } catch (e) { return null; }
  }
  function cacheSet(key, v) {
    try { sessionStorage.setItem(key, JSON.stringify({ t: Date.now(), v: v })); } catch (e) {}
  }

  /* -> Promise<{ values: [...sizes], vendors: [...brands] }> for one collection */
  function loadFeed(handle) {
    if (feeds[handle]) return feeds[handle];
    var key = 'si-feed:' + handle;
    var cached = cacheGet(key);
    if (cached) return (feeds[handle] = Promise.resolve(cached));

    var values = {}, vendors = {};
    function page(p) {
      return fetch('/collections/' + encodeURIComponent(handle) + '?view=si-feed&page=' + p,
                   { credentials: 'same-origin' })
        .then(function (r) { return r.text(); })
        .then(function (txt) {
          var d = JSON.parse(txt.slice(txt.indexOf('{'), txt.lastIndexOf('}') + 1));
          (d.values || []).forEach(function (v) { if (v) values[v] = 1; });
          (d.vendors || []).forEach(function (v) { if (v) vendors[v] = 1; });
          if (d.page < d.pages) return page(d.page + 1);
          var out = { values: Object.keys(values), vendors: Object.keys(vendors) };
          cacheSet(key, out);
          return out;
        });
    }
    feeds[handle] = page(1).catch(function (e) { delete feeds[handle]; throw e; });
    return feeds[handle];
  }

  function esc(s) {
    return String(s).replace(/[&<>"]/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c];
    });
  }

  /* ================= 1. Shop by Size ================= */

  var EU_MAP = { '36': [4, 4.5], '37.5': [4.5, 5, 5.5], '40': [7, 7.5], '42': [8.5, 9, 9.5], '47': [14] };
  var CLOTH = {
    'XXS': ['XXS'], 'XS': ['XS'], 'S': ['Small'], 'M': ['Medium'], 'L': ['Large'],
    'XL': ['XL'], 'XXL': ['XXL'], 'XXXL': ['XXXL'],
    'S/M': ['Small', 'Medium'], 'M/L': ['Medium', 'Large'], 'L/XL': ['Large', 'XL'], 'XL/XXL': ['XL', 'XXL'],
    'EXTRA SMALL': ['XS'], 'SMALL': ['Small'], 'MEDIUM': ['Medium'], 'LARGE': ['Large'],
    'EXTRA LARGE': ['XL'], 'EXTRA EXTRA LARGE': ['XXL'],
    '28': ['XS', 'Small'], '29': ['Small'], '30': ['Small'], '31': ['Small', 'Medium'],
    '32': ['Small', 'Medium'], '33': ['Medium', 'Large'], '34': ['Large'], '35': ['Large'],
    '36': ['Large', 'XL'], '38': ['XL', 'XXL'], '40': ['XXL'], '42': ['XXL', 'XXXL']
  };
  var CLOTH_ORDER = ['XXS', 'XS', 'Small', 'Medium', 'Large', 'XL', 'XXL', 'XXXL'];
  var MW = 1.5;
  var YOUTH_ADULT = 3.5;

  function fmt(n) { return String(Math.round(n * 2) / 2); }

  /* -> array of [group, key]. group: S shoe, K kids, C clothing, O one size, X other */
  function classify(raw) {
    var core = String(raw).split(' - ')[0].trim();
    var up = core.toUpperCase();
    var m;

    if ((m = up.match(/^EU\s*(\d+(?:\.5)?)$/))) {
      var t = EU_MAP[m[1]];
      return t ? t.map(function (u) { return ['S', fmt(u)]; }) : [['X', raw]];
    }
    /* youth first, so "7Y/8.5W" can never be misread as a women's size */
    if ((m = up.match(/^(\d+(?:\.5)?)Y(?:\s*\/.*)?$/))) {
      var y = +m[1];
      return y >= YOUTH_ADULT ? [['S', fmt(y)]] : [['K', fmt(y) + 'Y']];
    }
    if ((m = up.match(/^(\d+(?:\.5)?)M\s*\/\s*(\d+(?:\.5)?)W$/))) {
      var u = +m[1];
      return u > 17 ? [['X', raw]] : [['S', fmt(u)]];
    }
    if ((m = up.match(/^(\d+(?:\.5)?)W\s*\/\s*(\d+(?:\.5)?)M$/))) return [['S', fmt(+m[2])]];
    if ((m = up.match(/^(\d+(?:\.5)?)W$/))) return [['S', fmt(+m[1] - MW)]];
    if ((m = up.match(/^(\d+(?:\.5)?)([CK])$/))) return [['K', fmt(+m[1]) + m[2]]];
    if (up === 'OS' || up === 'ONE SIZE' || up === 'FREE SIZE') return [['O', 'One Size']];

    var tok = up.split(' (')[0].trim();
    if (CLOTH[tok]) return CLOTH[tok].map(function (g) { return ['C', g]; });

    if ((m = up.match(/^(\d+(?:\.5)?)$/))) {
      var n = +m[1];
      if (n > 0 && n <= 17) return [['S', fmt(n)]];
      var g = CLOTH[m[1]];
      if (g) return g.map(function (x) { return ['C', x]; });
    }
    return [['X', raw]];
  }

  function kidsSortKey(k) {
    var m = k.match(/^(\d+(?:\.5)?)([CKY])$/);
    return m ? ({ C: 0, K: 1, Y: 2 }[m[2]] * 1000 + (+m[1])) : 9e9;
  }

  function buildGroups(values) {
    var G = { S: {}, K: {}, C: {}, O: {}, X: {} };
    values.forEach(function (v) {
      classify(v).forEach(function (pair) {
        (G[pair[0]][pair[1]] = G[pair[0]][pair[1]] || []).push(v);
      });
    });
    return G;
  }

  function currentState() {
    var q = new URLSearchParams(location.search);
    var active = {};
    q.getAll('filter.v.option.size').forEach(function (v) { active[v] = 1; });
    var keep = new URLSearchParams();
    q.forEach(function (val, k) {
      if (k !== 'filter.v.option.size' && k !== 'page') keep.append(k, val);
    });
    return { active: active, keep: keep };
  }

  /* size links always ask for in-stock variants, so a size never shows a pair
     whose THAT size is sold out even if other sizes of it are available */
  function hrefFor(values, keep) {
    var p = new URLSearchParams();
    values.forEach(function (v) { p.append('filter.v.option.size', v); });
    keep.forEach(function (val, k) { p.append(k, val); });
    if (!p.has('filter.v.availability')) p.append('filter.v.availability', '1');
    return location.pathname + '?' + p.toString();
  }

  function button(labelHtml, values, st) {
    var isActive = values.some(function (v) { return st.active[v]; });
    return '<a class="si-size' + (isActive ? ' si-size--active' : '') + '" href="' +
           esc(hrefFor(values, st.keep)) + '"><span class="si-size__label">' + labelHtml + '</span></a>';
  }
  function shoeLabel(s) {
    return '<span class="si-size__m">Size ' + esc(s) + ' Mens</span><span class="si-size__sep"> / </span>' +
           '<span class="si-size__w">' + esc(fmt(+s + MW)) + ' Womens</span>';
  }
  function group(title, html) {
    return html ? '<div class="si-sizes__group"><p class="si-sizes__title">' + title +
                  '</p><div class="si-sizes__list">' + html + '</div></div>' : '';
  }

  function renderSizes(root, G) {
    var st = currentState();
    var out = '';
    if (Object.keys(st.active).length) {
      var k = st.keep.toString();
      out += '<a class="si-sizes__clear" href="' + esc(location.pathname + (k ? '?' + k : '')) + '">Clear sizes</a>';
    }
    var shoe = Object.keys(G.S).sort(function (a, b) { return a - b; })
      .map(function (s) { return button(shoeLabel(s), G.S[s], st); }).join('');
    var kids = Object.keys(G.K).sort(function (a, b) { return kidsSortKey(a) - kidsSortKey(b); })
      .map(function (s) { return button(esc(s), G.K[s], st); }).join('');
    var cloth = CLOTH_ORDER.filter(function (g) { return G.C[g]; })
      .map(function (g) { return button(esc(g), G.C[g], st); }).join('');
    var other = Object.keys(G.O).map(function (s) { return button(esc(s), G.O[s], st); }).join('') +
                Object.keys(G.X).sort().map(function (s) { return button(esc(s), G.X[s], st); }).join('');
    out += group('Shoe size', shoe) + group('Kids &amp; toddler', kids) +
           group('Clothing size', cloth) + group('Other', other);
    root.innerHTML = out;
    root.setAttribute('data-si-enhanced', 'complete');
  }

  var pageHandle = (location.pathname.match(/^\/collections\/([^\/?#]+)\/?$/) || [])[1];
  if (pageHandle) pageHandle = decodeURIComponent(pageHandle);

  function enhanceSizes() {
    if (!pageHandle) return;
    var roots = document.querySelectorAll('[data-si-size-groups]:not([data-si-enhanced])');
    if (!roots.length) return;
    Array.prototype.forEach.call(roots, function (r) { r.setAttribute('data-si-enhanced', 'loading'); });
    loadFeed(pageHandle).then(function (feed) {
      var G = buildGroups(feed.values);
      Array.prototype.forEach.call(roots, function (r) { renderSizes(r, G); });
    }).catch(function () {
      /* keep the server-rendered buttons as a fallback */
      Array.prototype.forEach.call(roots, function (r) { r.setAttribute('data-si-enhanced', 'fallback'); });
    });
  }

  /* ================= 2. Brand menus ================= */

  function pruneBrands() {
    var links = document.querySelectorAll('a[data-si-brand]:not([data-si-checked])');
    if (!links.length) return;
    var byHandle = {};
    Array.prototype.forEach.call(links, function (a) {
      a.setAttribute('data-si-checked', '1');
      var h = a.getAttribute('data-si-brand-collection');
      (byHandle[h] = byHandle[h] || []).push(a);
    });
    Object.keys(byHandle).forEach(function (h) {
      loadFeed(h).then(function (feed) {
        var sellable = {};
        feed.vendors.forEach(function (v) { sellable[v] = 1; });
        byHandle[h].forEach(function (a) {
          var spellings = (a.getAttribute('data-si-brand-spellings') || '').split('|');
          var any = spellings.some(function (s) { return sellable[s]; });
          if (!any && a.parentElement) a.parentElement.hidden = true;
        });
      }).catch(function () { /* leave the server list as-is */ });
    });
  }

  /* ================= wiring ================= */

  window.SoleInspires = { classify: classify };

  function run() { enhanceSizes(); pruneBrands(); }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', run);
  else run();

  /* Prestige re-renders facets and menus via AJAX; pick up anything new */
  new MutationObserver(run).observe(document.documentElement, { childList: true, subtree: true });
})();
/* ===== Sole Inspires: menu panels (sizes + brands), self-updating ===== */
(function(){
  "use strict";
  var MENUS = {
    footwear: { group: "S", sizeTitle: "Mens / Womens size", viewAll: "View all footwear" },
    apparel:  { group: "C", sizeTitle: "Size",               viewAll: "View all apparel" }
  };
  var CLOTH_ORDER = ["XXS","XS","Small","Medium","Large","XL","XXL","XXXL"];
  var CACHE_MS = 18e5, feeds = {};

  var CSS = [
    ".si-panel{list-style:none;padding:8px 0;display:flex;flex-direction:column}",
    ".si-panel__section--sizes{order:-1}",
    ".si-panel__section{border:0;margin:0 0 20px}",
    ".si-panel__title{display:flex;justify-content:space-between;align-items:center;cursor:pointer;list-style:none;font-size:12px;font-weight:600;letter-spacing:.12em;text-transform:uppercase;padding:6px 0 12px}",
    ".si-panel__title::-webkit-details-marker{display:none}",
    ".si-panel__title::after{content:'+';font-weight:400;font-size:18px;letter-spacing:0}",
    ".si-panel__section[open] > .si-panel__title::after{content:'\\2212'}",
    ".si-panel__brands{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:4px 24px;list-style:none;margin:0;padding:0}",
    ".si-panel__brands > li > a{display:block;padding:6px 0;font-size:15px !important;letter-spacing:0 !important;text-transform:none !important;font-family:inherit}",
    ".si-panel__sizes{display:grid;grid-template-columns:repeat(5,minmax(0,1fr));gap:8px}",
    ".si-panel__size{display:flex !important;flex-direction:column !important;align-items:center;justify-content:center;min-height:52px;padding:6px 2px;border:1px solid rgba(0,0,0,.18);text-decoration:none !important;line-height:1.2;font-size:15px}",
    ".si-panel__size small{display:block;font-size:11px;opacity:.6}",
    ".si-panel__size:hover{border-color:currentColor}",
    ".si-panel__more{display:inline-block;margin-top:14px;font-size:12px;font-weight:600;letter-spacing:.12em;text-transform:uppercase;text-decoration:underline;text-underline-offset:4px}",
    ".si-panel__all{display:block;border-top:1px solid rgba(0,0,0,.12);padding-top:16px;font-size:12px;font-weight:600;letter-spacing:.12em;text-transform:uppercase}",
    "@media (min-width:1000px){",
    ".header__dropdown-menu[data-si-panel]{width:min(900px,92vw);max-width:none;padding:28px 32px}",
    ".header__dropdown-menu[data-si-panel] .si-panel{display:grid;grid-template-columns:minmax(0,1fr) minmax(0,1.3fr);gap:0 48px}",
    ".header__dropdown-menu[data-si-panel] .si-panel__brands{grid-template-columns:repeat(3,minmax(0,1fr))}",
    ".header__dropdown-menu[data-si-panel] .si-panel__title{cursor:default;pointer-events:none}",
    ".header__dropdown-menu[data-si-panel] .si-panel__title::after{content:none}",
    ".header__dropdown-menu[data-si-panel] .si-panel__all{grid-column:1 / -1}",
    "}"
  ].join("");

  function addCss(){
    if (document.getElementById("si-panel-css")) return;
    var s = document.createElement("style"); s.id = "si-panel-css"; s.textContent = CSS;
    document.head.appendChild(s);
  }
  function cacheGet(k){ try{ var o=JSON.parse(sessionStorage.getItem(k)||"null"); return o&&Date.now()-o.t<CACHE_MS?o.v:null; }catch(e){ return null; } }
  function cacheSet(k,v){ try{ sessionStorage.setItem(k,JSON.stringify({t:Date.now(),v:v})); }catch(e){} }
  function esc(s){ return String(s).replace(/[&<>"]/g,function(c){return {"&":"&amp;","<":"&lt;",">":"&gt;",'"':"&quot;"}[c];}); }
  function loadFeed(h){
    if (feeds[h]) return feeds[h];
    var key = "si-feed:" + h, cached = cacheGet(key);
    if (cached) return feeds[h] = Promise.resolve(cached);
    var values = {}, vendors = {};
    function page(p){
      return fetch("/collections/" + encodeURIComponent(h) + "?view=si-feed&page=" + p, {credentials:"same-origin"})
        .then(function(r){ return r.text(); })
        .then(function(t){
          var d = JSON.parse(t.slice(t.indexOf("{"), t.lastIndexOf("}") + 1));
          (d.values||[]).forEach(function(v){ if (v) values[v] = 1; });
          (d.vendors||[]).forEach(function(v){ if (v) vendors[v] = 1; });
          if (d.page < d.pages) return page(d.page + 1);
          var out = { values: Object.keys(values), vendors: Object.keys(vendors) };
          cacheSet(key, out); return out;
        });
    }
    return feeds[h] = page(1).catch(function(e){ delete feeds[h]; throw e; });
  }
  function linkFor(h, values){
    var p = new URLSearchParams();
    values.forEach(function(v){ p.append("filter.v.option.size", v); });
    p.append("filter.v.availability", "1");
    return "/collections/" + encodeURIComponent(h) + "?" + p.toString();
  }
  function sizesHtml(h, feed){
    var classify = window.SoleInspires && window.SoleInspires.classify;
    if (!classify) return "";
    var g = MENUS[h].group, G = {};
    feed.values.forEach(function(v){
      classify(v).forEach(function(pair){ if (pair[0] === g) (G[pair[1]] = G[pair[1]] || []).push(v); });
    });
    var keys = g === "S" ? Object.keys(G).sort(function(a,b){ return a - b; }) : CLOTH_ORDER.filter(function(k){ return G[k]; });
    return keys.map(function(k){
      var inner = g === "S" ? esc(k) + "<small>" + esc(String(Math.round((+k + 1.5) * 2) / 2)) + "W</small>" : esc(k);
      return '<a class="si-panel__size" href="' + esc(linkFor(h, G[k])) + '">' + inner + "</a>";
    }).join("");
  }

  function build(ul, h){
    var brandLis = [], otherLis = [];
    Array.prototype.forEach.call(ul.children, function(li){
      if (li.querySelector("a[data-si-brand]")) brandLis.push(li); else otherLis.push(li);
    });
    var panel = document.createElement("li");
    panel.className = "si-panel";
    var b = document.createElement("details");
    b.className = "si-panel__section";
    b.open = true;
    b.innerHTML = '<summary class="si-panel__title">Brands</summary><ul class="si-panel__brands" role="list"></ul>';
    var list = b.querySelector("ul");
    brandLis.forEach(function(li){ list.appendChild(li); });
    panel.appendChild(b);
    var s = document.createElement("details");
    s.className = "si-panel__section si-panel__section--sizes";
    s.open = true;
    s.hidden = true;
    s.innerHTML = '<summary class="si-panel__title">' + esc(MENUS[h].sizeTitle) + '</summary><div class="si-panel__sizes"></div>' +
      '<a class="si-panel__more" href="/collections/' + encodeURIComponent(h) + '">View all sizes</a>';
    panel.appendChild(s);
    var all = document.createElement("a");
    all.className = "si-panel__all";
    all.href = "/collections/" + encodeURIComponent(h);
    all.textContent = MENUS[h].viewAll;
    panel.appendChild(all);
    otherLis.forEach(function(li){ li.hidden = true; });
    ul.appendChild(panel);
    ul.setAttribute("data-si-panel", "done");
  }
  function fillSizes(ul, h, feed){
    var s = ul.querySelector(".si-panel__section--sizes");
    if (!s) return;
    var html = sizesHtml(h, feed);
    if (!html) return;
    s.querySelector(".si-panel__sizes").innerHTML = html;
    s.hidden = false;
  }

  function run(){
    Object.keys(MENUS).forEach(function(h){
      var lists = [];
      document.querySelectorAll('a[data-si-brand-collection="' + h + '"]').forEach(function(a){
        var ul = a.closest("ul");
        if (ul && ul.classList.contains("si-panel__brands")) return;
        if (ul && !ul.hasAttribute("data-si-panel") && lists.indexOf(ul) < 0) lists.push(ul);
      });
      if (!lists.length) return;
      addCss();
      lists.forEach(function(ul){ build(ul, h); });
      loadFeed(h).then(function(feed){
        lists.forEach(function(ul){ fillSizes(ul, h, feed); });
      }).catch(function(){});
    });
  }
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", run); else run();
  new MutationObserver(run).observe(document.documentElement, { childList: true, subtree: true });
})();

/* ===== Sole Inspires: keep wide menu panels inside the screen ===== */
(function(){
  function fit(ul){
    ul.style.marginLeft = "";
    var r = ul.getBoundingClientRect(), over = r.right - (window.innerWidth - 16);
    if (over > 0) ul.style.marginLeft = (-Math.min(over, r.left - 16)) + "px";
  }
  document.addEventListener("toggle", function(e){
    var d = e.target;
    if (d.open && d.matches && d.matches("details.header__menu-disclosure")) {
      var ul = d.querySelector(":scope > .header__dropdown-menu[data-si-panel]");
      if (ul) { fit(ul); setTimeout(function(){ fit(ul); }, 60); }
    }
  }, true);
})();
/* ===== Sole Inspires: size buttons – highlight only the size picked, right groups per collection ===== */
(function(){
  var HIDE = { apparel: ["Shoe size", "Kids & toddler"], footwear: ["Clothing size"] };
  function fix(){
    var h = (location.pathname.match(/^\/collections\/([^\/?#]+)\/?$/) || [])[1];
    var active = new URLSearchParams(location.search).getAll("filter.v.option.size");
    document.querySelectorAll('[data-si-size-groups][data-si-enhanced="complete"]:not([data-si-fixed])').forEach(function(root){
      root.setAttribute("data-si-fixed", "1");
      if (h && HIDE[h]) root.querySelectorAll(".si-sizes__group").forEach(function(g){
        var t = g.querySelector(".si-sizes__title");
        if (t && HIDE[h].indexOf(t.textContent.trim()) > -1) g.remove();
      });
      root.querySelectorAll("a.si-size").forEach(function(a){
        var vals = new URL(a.getAttribute("href"), location.origin).searchParams.getAll("filter.v.option.size");
        var on = vals.length > 0 && vals.every(function(v){ return active.indexOf(v) > -1; });
        a.classList.toggle("si-size--active", on);
      });
    });
  }
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", fix); else fix();
  new MutationObserver(fix).observe(document.documentElement, { childList: true, subtree: true });
})();
/* ===== Sole Inspires: size buttons – pick several, instant, no reload, no jump (updated 22 Sep 2026, v4) =====
   Replaces the two earlier blocks "pick more than one size" and
   "size buttons update without reloading or jumping".
   - Tap to add a size, tap again to remove it (shows products in ANY picked size).
   - The tapped box turns on/off instantly; fast taps stack correctly.
   - Only the products and filters refresh (theme's own filter update), no page reload.
   - The page and the phone size row stay where they were.
   - The size boxes stay on screen during an update (never swapped out under a finger),
     so every tap registers the first time.
   - On phones the tap is handled the moment the finger lifts (iPhone Safari can
     ignore taps while the page is updating), and double-tap zoom is off on these
     buttons so quick taps count.
   - Never pulls the size row or page back once the shopper touches or scrolls.
   Functionality only: no visual changes. */
(function(){
  "use strict";
  var KEY = "filter.v.option.size", SEL = "[data-si-size-groups]";
  var SECTIONS = ".shopify-section--main-collection, .shopify-section--main-search";
  var seen = new WeakSet();

  function sizesOf(href){
    try { return new URL(href, location.origin).searchParams.getAll(KEY); } catch(e){ return []; }
  }
  function own(a){ return (a.getAttribute("data-si-vals") || "").split("\n").filter(Boolean); }
  function isOn(vals, active){ return vals.length > 0 && vals.every(function(v){ return active.indexOf(v) > -1; }); }

  /* What the picked sizes become if this button is tapped now. */
  function nextSizes(a, active){
    var mine = own(a);
    if (isOn(mine, active)) {
      var keep = {}, root = a.closest(SEL);
      Array.prototype.forEach.call(root ? root.querySelectorAll("a.si-size[data-si-vals]") : [], function(o){
        if (o === a) return;
        var ov = own(o);
        if (isOn(ov, active)) ov.forEach(function(v){ keep[v] = 1; });
      });
      return { on: false, sizes: active.filter(function(v){ return mine.indexOf(v) < 0 || keep[v]; }) };
    }
    var next = active.slice();
    mine.forEach(function(v){ if (next.indexOf(v) < 0) next.push(v); });
    return { on: true, sizes: next };
  }
  function urlFor(sizes, q){
    var p = new URLSearchParams();
    sizes.forEach(function(v){ p.append(KEY, v); });
    q.forEach(function(val, k){ if (k !== KEY && k !== "page" && k !== "section_id") p.append(k, val); });
    if (sizes.length && !p.has("filter.v.availability")) p.append("filter.v.availability", "1");
    var s = p.toString();
    return location.pathname + (s ? "?" + s : "");
  }

  /* Prepare a finished size box: remember each button's own sizes, set its link. */
  function ready(root){
    return !root.hasAttribute("data-si-multi") &&
      root.getAttribute("data-si-enhanced") === "complete" &&
      root.hasAttribute("data-si-fixed");
  }
  function apply(root){
    root.setAttribute("data-si-multi", "1");
    live[keyOf(root)] = root;
    var q = new URLSearchParams(location.search), active = q.getAll(KEY);
    var btns = root.querySelectorAll("a.si-size");
    Array.prototype.forEach.call(btns, function(a){
      if (!a.hasAttribute("data-si-vals")) a.setAttribute("data-si-vals", sizesOf(a.getAttribute("href")).join("\n"));
    });
    Array.prototype.forEach.call(btns, function(a){
      if (!own(a).length) return;
      var n = nextSizes(a, active);
      a.setAttribute("href", urlFor(n.sizes, q));
      a.setAttribute("aria-pressed", n.on ? "false" : "true");
    });
  }
  /* Which size box this is: phone row, filter drawer or desktop sidebar. */
  var live = {};
  function keyOf(root){
    if (root.closest(".si-strip")) return "strip";
    if (root.closest("facets-drawer")) return "drawer";
    if (root.closest("safe-sticky")) return "sidebar";
    return "other";
  }
  /* Bring an existing size box up to date with the current filters. */
  function refresh(root){
    var q = new URLSearchParams(location.search), active = q.getAll(KEY);
    Array.prototype.forEach.call(root.querySelectorAll("a.si-size[data-si-vals]"), function(a){
      var vals = own(a);
      if (!vals.length) return;
      a.classList.toggle("si-size--active", isOn(vals, active));
      var n = nextSizes(a, active);
      a.setAttribute("href", urlFor(n.sizes, q));
      a.setAttribute("aria-pressed", n.on ? "false" : "true");
    });
    var clear = root.querySelector(":scope > a.si-sizes__clear");
    if (active.length && !clear) {
      clear = document.createElement("a");
      clear.className = "si-sizes__clear";
      clear.textContent = "Clear sizes";
      root.insertBefore(clear, root.firstChild);
    }
    if (clear) {
      if (active.length) clear.setAttribute("href", urlFor([], q)); else clear.remove();
    }
  }
  function watch(root){
    if (seen.has(root)) return;
    seen.add(root);
    if (ready(root)) { apply(root); return; }
    var mo = new MutationObserver(function(){
      if (ready(root)) { mo.disconnect(); apply(root); }
    });
    mo.observe(root, { attributes: true, attributeFilter: ["data-si-enhanced", "data-si-fixed"] });
  }
  function scan(node){
    if (node.nodeType !== 1) return;
    if (node.matches(SEL)) watch(node);
    Array.prototype.forEach.call(node.querySelectorAll(SEL), watch);
  }

  /* Skip the theme's automatic scroll to the product list after a size tap. */
  var skipUntil = 0, keepPos = null;
  var origScroll = Element.prototype.scrollIntoView;
  Element.prototype.scrollIntoView = function(){
    if (Date.now() < skipUntil && this.classList && this.classList.contains("collection")) { skipUntil = 0; return; }
    return origScroll.apply(this, arguments);
  };
  function restore(){
    if (!keepPos || Date.now() > keepPos.until) return;
    if (Math.abs(window.scrollY - keepPos.y) > 60) window.scrollTo(0, keepPos.y);
    if (keepPos.inStrip && keepPos.x > 1) {
      var list = document.querySelector(".si-strip .si-sizes__list");
      if (list && list.scrollLeft < 1) list.scrollLeft = keepPos.x;
    }
  }
  /* The moment the shopper touches, scrolls or swipes again, stop restoring. */
  function stopRestore(){ keepPos = null; }
  ["wheel", "keydown"].forEach(function(ev){ document.addEventListener(ev, stopRestore, { capture: true, passive: true }); });

  /* Double-tap zoom off on the size buttons, so two quick taps are two taps. */
  var st = document.createElement("style");
  st.textContent = "a.si-size,a.si-sizes__clear{touch-action:manipulation;-webkit-tap-highlight-color:transparent}";
  document.head.appendChild(st);

  /* Do what a tap on a size button (or "Clear sizes") should do. */
  function act(a){
    var section = a.closest(SECTIONS);
    if (!section || section.id.indexOf("shopify-section-") !== 0) return false;

    /* Work out the new sizes from what is picked right now (not from the old link). */
    var q = new URLSearchParams(location.search), active = q.getAll(KEY), target;
    if (a.classList.contains("si-sizes__clear")) {
      target = urlFor([], q);
      Array.prototype.forEach.call(section.querySelectorAll("a.si-size--active"), function(b){ b.classList.remove("si-size--active"); });
    } else if (a.hasAttribute("data-si-vals")) {
      var n = nextSizes(a, active), vals = a.getAttribute("data-si-vals");
      target = urlFor(n.sizes, q);
      /* Instant feedback: switch this size on/off in every size box right away. */
      Array.prototype.forEach.call(section.querySelectorAll("a.si-size[data-si-vals]"), function(b){
        if (b.getAttribute("data-si-vals") === vals) b.classList.toggle("si-size--active", n.on);
      });
    } else {
      target = a.getAttribute("href");
    }

    var row = a.closest(".si-sizes__list");
    keepPos = { y: window.scrollY, x: row ? row.scrollLeft : 0, inStrip: !!a.closest(".si-strip"), until: Date.now() + 1500 };
    skipUntil = Date.now() + 3000;
    var mo = new MutationObserver(function(){
      mo.disconnect();
      restore();
      [0, 100].forEach(function(ms){ setTimeout(restore, ms); });
    });
    mo.observe(section, { childList: true });

    var url = new URL(target, location.origin);
    url.searchParams.set("section_id", section.id.replace("shopify-section-", ""));
    section.dispatchEvent(new CustomEvent("facet:update", { bubbles: true, detail: { url: url } }));
    return true;
  }
  function sizeLink(e){ return e.target.closest && e.target.closest("a.si-size, a.si-sizes__clear"); }

  /* Phones: handle the tap when the finger lifts (not a swipe, not stopping a scroll). */
  var down = null, handled = null, lastRowScroll = 0;
  document.addEventListener("scroll", function(e){
    if (e.target && e.target.classList && e.target.classList.contains("si-sizes__list")) lastRowScroll = Date.now();
  }, { capture: true, passive: true });
  document.addEventListener("pointerdown", function(e){
    if (e.pointerType === "mouse") return;
    stopRestore();
    var a = sizeLink(e);
    down = (a && Date.now() - lastRowScroll > 120) ? { a: a, x: e.clientX, y: e.clientY, t: Date.now() } : null;
  }, true);
  document.addEventListener("pointercancel", function(){ down = null; }, true);
  document.addEventListener("pointerup", function(e){
    var d = down; down = null;
    if (!d || e.pointerType === "mouse") return;
    if (Math.abs(e.clientX - d.x) > 10 || Math.abs(e.clientY - d.y) > 10 || Date.now() - d.t > 800) return;
    if (act(d.a)) handled = { a: d.a, until: Date.now() + 800 };
  }, true);

  /* Mouse clicks (and the phone's own click, which is skipped if already handled). */
  document.addEventListener("click", function(e){
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    var a = sizeLink(e);
    if (!a || !a.closest(SECTIONS)) return;
    e.preventDefault();
    if (handled && handled.a === a && Date.now() < handled.until) { handled = null; return; }
    act(a);
  });

  function init(){
    Array.prototype.forEach.call(document.querySelectorAll(SECTIONS), function(section){
      scan(section);
      /* When a filter changes, the theme swaps the section's contents.
         Watch only that top level (not everything inside it). */
      new MutationObserver(function(muts){
        for (var i = 0; i < muts.length; i++) {
          var added = muts[i].addedNodes;
          for (var j = 0; j < added.length; j++) {
            var node = added[j];
            if (node.nodeType !== 1) continue;
            /* Put the size boxes that were already on screen back in place of the
               fresh (empty) copies, then just update which sizes are picked. */
            var fresh = node.matches(SEL) ? [node] : Array.prototype.slice.call(node.querySelectorAll(SEL));
            fresh.forEach(function(r){
              var old = live[keyOf(r)];
              if (old && old !== r && old.hasAttribute("data-si-multi") && !old.contains(r)) {
                r.replaceWith(old);
                refresh(old);
              } else {
                watch(r);
              }
            });
          }
        }
      }).observe(section, { childList: true });
    });
  }
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", init); else init();
})();
/* ===== Sole Inspires: hide size chips above the products (added 21 Sep 2026) =====
   The black size buttons already show which sizes are picked, so the long list of
   "Size: ..." chips (and the automatic "Availability: In stock" chip) is hidden.
   Brand, price and other chips still show. Functionality only. */
(function(){
  "use strict";
  var HIDE = /^(Size|Availability)\s*:/i;
  function tidy(scope){
    Array.prototype.forEach.call(scope.querySelectorAll(".active-facets"), function(box){
      var shown = 0;
      Array.prototype.forEach.call(box.querySelectorAll(".removable-facet"), function(chip){
        var label = chip.querySelector("span");
        if (label && HIDE.test(label.textContent.trim())) chip.style.display = "none"; else shown++;
      });
      box.style.display = shown ? "" : "none";
    });
  }
  function init(){
    var sections = document.querySelectorAll(".shopify-section--main-collection, .shopify-section--main-search");
    Array.prototype.forEach.call(sections, function(section){
      tidy(section);
      /* Runs again only when the theme swaps the section after a filter change. */
      new MutationObserver(function(){ tidy(section); }).observe(section, { childList: true });
    });
  }
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", init); else init();
})();
/* ===== Sole Inspires: keep picked sizes when another filter changes (added 21 Sep 2026) =====
   The theme's filter panel only remembers its own boxes (brand, price, type...),
   so it used to drop the sizes picked with the size buttons. This quietly adds the
   picked sizes to the filter panel as hidden entries. Functionality only. */
(function(){
  "use strict";
  var KEY = "filter.v.option.size";
  function sync(scope){
    var sizes = new URLSearchParams(location.search).getAll(KEY);
    Array.prototype.forEach.call(scope.querySelectorAll("form.facets"), function(form){
      Array.prototype.forEach.call(form.querySelectorAll("input[data-si-keep-size]"), function(i){ i.remove(); });
      if (form.querySelector('input[name="' + KEY + '"]')) return;
      sizes.forEach(function(v){
        var i = document.createElement("input");
        i.type = "hidden"; i.name = KEY; i.value = v;
        i.setAttribute("data-si-keep-size", "");
        form.appendChild(i);
      });
    });
  }
  function init(){
    var sections = document.querySelectorAll(".shopify-section--main-collection, .shopify-section--main-search");
    Array.prototype.forEach.call(sections, function(section){
      sync(section);
      /* Runs again only when the theme swaps the section after a filter change. */
      new MutationObserver(function(){ sync(section); }).observe(section, { childList: true });
    });
  }
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", init); else init();
})();